'use client';

import clsx from 'clsx';
import React, { type ChangeEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SingleValue } from 'react-select';
import { isPdfFile } from '@/app/(main)/client-detail/utils/isPdfFile';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { Spinner } from '@/app/components/Spinner';
import { ViewFileByType } from '@/app/components/ViewFileByType';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import PdfPreview from '@/app/components/cards/FileCard/PdfPreview';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { useIsClientProfileForm } from '@/context/ClientProfileFormProvider';
import { useCountrySelector } from '@/hooks/useFormCountryList/useFormCountryList';
import type { ClientDocumentWithPassportData, PassportDataWithName } from '@/types/clientsApiTypes';
import { fileTypes } from '@/types/common';
import { DateInputWithFooter } from '../../create/components/DateInputWithFooter';
import { ProxiedPdfPreview } from '../ProxiedPdfPreview';
import { ViewDocumentModalLoading } from './loading';
import TrashIcon from '/public/icons/trash.svg';

const DOCUMENT_CONTENT_SIZE = 252;

interface ViewDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (doc: ClientDocumentWithPassportData) => void;
  onUpdateDocuments?: (documents: PassportDataWithName) => void;
  document: ClientDocumentWithPassportData | null;
  fieldPrefix: string;
  isLoading?: boolean;
}

export const ViewDocumentModal = ({ isLoading = false, ...props }: ViewDocumentModalProps) => {
  if (isLoading) {
    return <ViewDocumentModalLoading isOpen={props.isOpen} onClose={props.onClose} />;
  }

  return <ViewDocumentModalContent {...props} />;
};

function ViewDocumentModalContent({
  isOpen,
  onClose,
  onDelete,
  document,
  fieldPrefix,
  onUpdateDocuments,
}: Omit<ViewDocumentModalProps, 'isLoading'>) {
  const [isImageLoading, setIsImageLoading] = useState(Boolean(document?.document_url));
  const isClientProfileForm = useIsClientProfileForm();

  const { countryOptions } = useCountrySelector({
    selectedId: document?.issuance_country,
    url: '/api/worldwide/countries',
  });

  const [passportData, setPassportData] = useState<PassportDataWithName>({
    document_name: document?.document_name ?? '',
    passport_number: document?.passport_number ?? '',
    issuance_country: document?.issuance_country ?? '',
    issue_date: document?.issue_date ?? '',
    expiry_date: document?.expiry_date ?? '',
  });

  const { register, setValue } = useFormContext();

  const handleImageLoad = () => setIsImageLoading(false);

  const handlePassportNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassportData((prev) => ({
      ...prev,
      passport_number: e.target.value,
    }));
  };

  const handlePassportIssueCountryChange = (value: SingleValue<SelectorOption<string>>) => {
    setPassportData((prev) => ({
      ...prev,
      issuance_country: value?.value ?? '',
    }));
  };

  const handlePassportIssueDateChange = (val: string) => {
    setPassportData((prev) => ({
      ...prev,
      issue_date: val,
    }));
  };

  const handlePassportExpiryDateChange = (val: string) => {
    setPassportData((prev) => ({
      ...prev,
      expiry_date: val,
    }));
  };

  const handleDocumentNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassportData((prev) => ({
      ...prev,
      document_name: e.target.value,
    }));
  };

  const handleDone = () => {
    setValue(`${fieldPrefix}.document_name`, passportData.document_name);
    setValue(`${fieldPrefix}.passport_number`, passportData.passport_number);
    setValue(`${fieldPrefix}.issuance_country`, passportData.issuance_country);
    setValue(`${fieldPrefix}.issue_date`, passportData.issue_date);
    setValue(`${fieldPrefix}.expiry_date`, passportData.expiry_date);
    onUpdateDocuments?.(passportData);
  };

  if (!document) return null;

  return (
    <Modal open={isOpen} onClose={onClose} variant="fullScreenMobile">
      <div className="flex flex-col items-center text-center w-full min-w-[600px] max-w-[600px] max-h-[90vh] md:overflow-y-auto max-md:h-full max-md:max-h-none max-md:min-w-0 max-md:max-w-none max-md:items-stretch max-md:text-left">
        <div className="px-[36px] pt-[46px] max-md:px-4 max-md:pt-4">
          <h2 className="light text-blue-900 whitespace-pre-line max-md:hidden">Document information</h2>
          <div className="hidden max-md:block">
            <ModalMobileHeader title="Document information" onBack={onClose} />
          </div>
          <p className="text-body-regular text-grey-700 text-center mb-4 md:mt-[6px] max-md:mt-4 max-md:mb-0 max-md:text-left max-md:text-body-small">
            Please label your document with the traveler&apos;s name and the document type so your agent can easily
            distinguish between multiple documents.
          </p>
        </div>
        <div className="overflow-y-auto px-[36px] max-md:min-h-0 max-md:flex-1 max-md:px-4 max-md:pt-5">
          <div className="flex flex-col w-full gap-6 max-md:gap-4 max-md:text-left">
            <Input
              required
              label="Document name"
              value={passportData.document_name ?? ''}
              name={`${fieldPrefix}.document_name`}
              onChange={handleDocumentNameChange}
            />
            <div className="grid grid-cols-2 gap-y-6 gap-x-5 w-full max-md:grid-cols-1 max-md:gap-4">
              <Input
                {...register(`${fieldPrefix}.passport_number`)}
                onChange={handlePassportNumberChange}
                label="Passport number"
                value={passportData.passport_number}
              />
              <Selector
                options={countryOptions}
                placeholder={passportData.issuance_country}
                className="text-left"
                {...register(`${fieldPrefix}.issuance_country`)}
                onChange={handlePassportIssueCountryChange}
                label="Passport issue country"
                value={passportData.issuance_country}
              />
              <DateInputWithFooter
                label="Date of Issue"
                name={`${fieldPrefix}.issue_date`}
                value={passportData.issue_date}
                onChangeDate={handlePassportIssueDateChange}
              />
              <DateInputWithFooter
                label="Date of Expiration"
                name={`${fieldPrefix}.expiry_date`}
                value={passportData.expiry_date}
                onChangeDate={handlePassportExpiryDateChange}
              />
            </div>
            {isImageLoading ? (
              <div className="flex justify-center items-center h-[252px]">
                <Spinner />
              </div>
            ) : null}
            <div
              className={clsx('flex w-full mt-6', {
                'h-0': isImageLoading,
                [`h-[${DOCUMENT_CONTENT_SIZE}px]`]: !isImageLoading,
              })}
            >
              {isPdfFile(document.document_url ?? '') && isClientProfileForm ? (
                <PdfPreview
                  fileUrl={document.document_url ?? ''}
                  containerWidth={DOCUMENT_CONTENT_SIZE}
                  containerHeight={DOCUMENT_CONTENT_SIZE}
                  onLoadSuccess={handleImageLoad}
                />
              ) : isPdfFile(document.document_url ?? '') ? (
                <ProxiedPdfPreview
                  fileUrl={document.document_url ?? ''}
                  filename={
                    document.document_name?.toLowerCase().endsWith('.pdf')
                      ? document.document_name
                      : `${document.document_name ?? 'document'}.pdf`
                  }
                  width={DOCUMENT_CONTENT_SIZE}
                  height={DOCUMENT_CONTENT_SIZE}
                  onLoad={handleImageLoad}
                />
              ) : (
                <ViewFileByType
                  onLoad={handleImageLoad}
                  type={fileTypes.IMAGE}
                  path={document.document_url ?? ''}
                  caption={document.document_name ?? ''}
                  name={document.document_name ?? ''}
                  contentDimensions={{
                    width: DOCUMENT_CONTENT_SIZE,
                    height: DOCUMENT_CONTENT_SIZE,
                  }}
                />
              )}
              <div className="flex-1" />
            </div>
          </div>
        </div>
        <div className="w-full gap-4 flex flex-col px-[36px] pb-[46px] pt-4 max-md:mt-auto max-md:shrink-0 max-md:gap-3 max-md:px-4 max-md:pb-6">
          <BaseButton
            intent="ghost_danger"
            onClick={() => onDelete(document)}
            size="ghost_small"
            startIcon={<TrashIcon />}
            className="self-start"
            type="button"
          >
            Delete Document
          </BaseButton>
          <div className="flex gap-5 w-full max-md:gap-3">
            <BaseButton intent="secondary" onClick={onClose} size="full">
              Cancel
            </BaseButton>
            <BaseButton intent="primary" onClick={handleDone} size="full">
              Save Changes
            </BaseButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}
